<script setup>
  import { useMenuStore } from '@stores/menu';
  import { useAuthStore } from '@stores/auth';
  import { useUiStore } from '@stores/ui';
  import { useWindowSize, onClickOutside } from '@vueuse/core';
  import CookieConsent from '@/components/cometa/cookies/CookieConsent.vue';
  import VerificationModal from '@/components/cometa/legitimuz/VerificationModal.vue';

  const menuStore = useMenuStore();
  const authStore = useAuthStore();
  const uiStore = useUiStore();
  const route = useRoute();
  const windowSize = useWindowSize();

  const showBackToTop = ref(false);
  const sidebarTarget = ref(null);
  const contentTarget = ref(null);
  const isSmallScreen = computed(() => windowSize.width.value < 1024);
  const isDesktop = computed(() => windowSize.width.value >= 768);

  const hideNavbar = computed(
    () => windowSize.width.value < 640 && menuStore.menuOpened && !menuStore.isCollapsed
  );

  const isSportsRoute = computed(() => {
    const fullPath = route.fullPath || window.location.pathname + window.location.hash;
    return fullPath.includes('/sports') || fullPath.includes('/altenar-sports');
  });

  const routersMenu = computed(() => {
    return /^\/user\/account$/.test(route.path) && authStore.isLoggedIn;
  });

  const scrollToTop = () => {
    contentTarget.value?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContentScroll = (event) => {
    showBackToTop.value = event.target.scrollTop > 400;
  };

  const handleMouseEnter = () => {
    if (isSportsRoute.value && !isSmallScreen.value) {
      menuStore.collapseMenu(false);
    }
  };

  const handleMouseLeave = () => {
    if (isSportsRoute.value && !isSmallScreen.value) {
      menuStore.collapseMenu(true);
    }
  };

  const handleModalClose = () => {
  uiStore.showVerificationModal = false;
}

  const showMenuLogger = computed(() => {
    return route.path.startsWith('/user/') && authStore.isLoggedIn;
  });

  onMounted(() => {
    contentTarget.value?.addEventListener('scroll', handleContentScroll);

    if (isSportsRoute.value) {
      menuStore.collapseMenu(true);
    }

    watch(() => route.name, scrollToTop);
    watch(() => uiStore.state.scrollToTop, scrollToTop);
    watch(
      windowSize.width,
      (currentWidth) => {
        if (currentWidth > 1023 && menuStore.menuOpened) {
          menuStore.toggle();
        }
      },
      { immediate: true }
    );
  });

  onClickOutside(sidebarTarget, () => {
    if (menuStore.menuOpened) {
      menuStore.toggle();
    }
  });

  const shouldShowBanner = computed(() => {
    const path = route.path;
    return (
      path === '/' ||
      path === '/casino' ||
      path.startsWith('/casino/') ||
      path.startsWith('/providers/')
    );
  });

  watch(isSportsRoute, menuStore.collapseMenu, { immediate: true });
</script>

<template>
  <div class="flex h-dvh max-h-dvh flex-col overflow-hidden bg-brand-background">
    <TopAd />
    <div class="flex items-center w-full bg-brand-navbar">
      <Navbar v-if="!hideNavbar" class="z-80 text-white border-b border-brand-separator" />
    </div>

    <div
      class="relative flex flex-1 flex-col lg:flex-row w-full overflow-hidden bg-brand-background font-sans"
    >
      <aside
        ref="sidebarTarget"
        class="duration-400 absolute top-0 z-50 h-full overflow-auto text-white transition-all lg:static"
        :class="[
          menuStore.isCollapsed ? 'w-20' : 'w-full sm:w-64',
          menuStore.menuOpened ? '' : '-translate-x-full lg:translate-x-0'
        ]"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <Menu />
      </aside>

      <main
        class="duration-400 flex w-full flex-1 flex-col overflow-hidden transition-all"
        :class="{ 'translate-x-full sm:translate-x-64': menuStore.menuOpened }"
      >
        <div
          ref="contentTarget"
          class="easy-in-out scrollbar-hide flex h-full flex-col items-center overflow-y-auto overflow-x-hidden text-white transition-all duration-1000"
        >
          <MenuLogger v-if="showMenuLogger" class="h-[87px] w-full" />

          <BaseContainer
            :full-width="
              ['sports-page', 'sports-esports', 'altenar-sports-page'].includes(route.name)
            "
          >
            <KeepAlive>
              <BaseBanner v-if="shouldShowBanner" />
            </KeepAlive>

            <CookieConsent />

            <div class="md:flex w-full !min-w-0">
              <Balance v-if="!menuStore.menuUser && routersMenu" class="md:hidden" />
              <MenuUser v-if="showMenuLogger"></MenuUser>

              <div class="flex-1 w-full" :class="showMenuLogger ? 'overflow-hidden' : ''">
                <keep-alive>
                  <NuxtPage />
                </keep-alive>
              </div>
            </div>
          </BaseContainer>
          <div
            class="flex w-full flex-1 justify-center bg-brand-footer lg:pb-6 border-t border-brand-separator"
          >
              <Footer />
          </div>
        </div>
      </main>

      <MenuMobile class="w-full lg:hidden" />
    </div>
  </div>

  <button
    @click="scrollToTop"
    class="backdrop-blur-2 duration-400 fixed bottom-14 left-1/2 z-20 flex -translate-x-1/2 translate-y-full select-none items-center gap-2 rounded-full bg-black/75 px-3 py-1 text-xs text-white transition-all"
    :class="{ '!-translate-y-[350%] lg:!-translate-y-full': showBackToTop }"
  >
    <Icon name="fa6-solid:arrow-up" size="0.8em" />
    Voltar ao topo
  </button>
  <VerificationModal
      :show="uiStore.showVerificationModal"
      @close="handleModalClose"
     />
  <DepositModal />
  <AuthModal />
  <BaseAlerts />
  <TermsAgeVerification />
</template>
